import React, { useEffect, useState, useCallback } from 'react';
import {
  IDSButton, IDSInput, IDSTextarea, IDSRadio, IDSSelect, IDSAlert, IDSDialog, IDSDatePicker,
  IDSCheckbox, IDSBadge,
} from '@inera/ids-react';
import ConfirmDialog from '../ConfirmDialog';
import StatusAlert from '../StatusAlert';
import {
  fetchSectionsByContract,
  createQuestionSection,
  updateQuestionSection,
  deleteQuestionSection,
  moveQuestionSection,
  addQuestion,
  updateQuestion,
  deleteQuestion,
  moveQuestion,
  linkSectionToContract,
  unlinkSectionFromContract,
} from '../../services/questionsAdminAPI';
import { fetchContracts } from '../../services/contractsAPI';

// Question types an admin can pick. SOURCE_SYSTEM and SERVICE_PROVIDER feed the Jira
// connection objects (AO/TPV) built when a report is submitted, and the backend finds
// them by type — so they must round-trip through this editor unchanged.
const QUESTION_TYPE_OPTIONS = [
  ['YES_NO', 'Ja/Nej'],
  ['TEXT', 'Fritext'],
  ['DATE', 'Datum'],
  ['TEXT_BLOCK', 'Textblock'],
  ['SOURCE_SYSTEM', 'Källsystem'],
  ['SERVICE_PROVIDER', 'Tjänsteproducent'],
];
const QUESTION_TYPE_LABELS = Object.fromEntries(QUESTION_TYPE_OPTIONS);
// Types whose answer is free text rather than a yes/no.
const TEXT_LIKE_TYPES = ['TEXT', 'DATE', 'SOURCE_SYSTEM', 'SERVICE_PROVIDER'];

// ── Small helpers ─────────────────────────────────────────────────────────────

const StatusMsg = ({ error, success }) => (
  <StatusAlert error={error} success={success} className="ids-mt-2" />
);

// ── Section create/edit form ──────────────────────────────────────────────────

const SCENARIO_OPTIONS = [
  { value: 'ALL',          label: 'Båda scenarierna' },
  { value: 'WEBAPP_ONLY',  label: 'Testrapport' },
  { value: 'LOCAL_ONLY',   label: 'Självdeklaration' },
];

const SectionForm = ({ initial = {}, onSave, onCancel, submitLabel = 'Spara' }) => {
  const [title, setTitle] = useState(initial.title || '');
  const [description, setDescription] = useState(initial.description || '');
  const [applicableScenario, setApplicableScenario] = useState(initial.applicableScenario || 'ALL');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [titleError, setTitleError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setTitleError('Titel krävs.'); return; }
    setTitleError(null);
    setLoading(true);
    setError(null);
    try {
      await onSave({ title: title.trim(), description: description.trim(), applicableScenario });
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      style={{ background: 'var(--ids-palette-neutral-95)', border: '1px solid var(--ids-palette-neutral-90)', borderRadius: '6px', padding: '1.25rem', marginBottom: '1rem' }}
    >
      <div className="ids-mb-3">
        <IDSInput label="Titel *" type="text" value={title} onChange={e => { setTitle(e.target.value); if (titleError) setTitleError(null); }} invalid={!!titleError} errorMsg={titleError} />
      </div>
      <div className="ids-mb-3">
        <IDSTextarea label="Beskrivning" rows={2} value={description} onChange={e => setDescription(e.target.value)} style={{ width: '100%', resize: 'vertical' }} />
      </div>
      <div className="ids-mb-3">
        <IDSSelect label="Visas i scenario" value={applicableScenario} onChange={e => setApplicableScenario(e.target.value)}>
          {SCENARIO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </IDSSelect>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <IDSButton size="s" type="submit" disabled={loading}>{loading ? 'Sparar...' : submitLabel}</IDSButton>
        <IDSButton size="s" type="button" onClick={onCancel}>Avbryt</IDSButton>
      </div>
      <StatusMsg error={error} />
    </form>
  );
};

// ── Question create/edit form ─────────────────────────────────────────────────

const QuestionForm = ({ sectionId, initial = null, onSave, onCancel, submitLabel = 'Spara' }) => {
  const [questionText, setQuestionText] = useState(initial?.questionText || '');
  const [questionType, setQuestionType] = useState(initial?.questionType || 'YES_NO');
  const [bodyText, setBodyText] = useState(initial?.bodyText || '');
  const [expectedAnswer, setExpectedAnswer] = useState(
    initial?.expectedAnswerBoolean != null ? String(initial.expectedAnswerBoolean) : 'true'
  );
  const [followupTrigger, setFollowupTrigger] = useState(initial?.followupConfig?.trigger || 'NONE');
  const [followupText, setFollowupText] = useState(initial?.followupConfig?.text || '');
  const [mandatory, setMandatory] = useState(initial?.mandatory !== false);
  const [info, setInfo] = useState(initial?.info || '');
  const [includeInTestResults, setIncludeInTestResults] = useState(initial?.includeInTestResults === true);
  const [testCaseName, setTestCaseName] = useState(initial?.testCaseName || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [textError, setTextError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!questionText.trim()) {
      setTextError(questionType === 'TEXT_BLOCK' ? 'Rubrik krävs.' : 'Frågetext krävs.');
      return;
    }
    setTextError(null);
    setLoading(true);
    setError(null);
    try {
      const payload = {
        questionText: questionText.trim(),
        questionType,
        info: info.trim() || null,
        includeInTestResults: questionType !== 'TEXT_BLOCK' && includeInTestResults,
        testCaseName: questionType !== 'TEXT_BLOCK' && includeInTestResults ? testCaseName.trim() : null,
        ...(questionType !== 'TEXT_BLOCK' && { mandatory }),
        ...(questionType === 'TEXT_BLOCK' && {
          bodyText: bodyText.trim() || null,
        }),
        ...(questionType === 'YES_NO' && {
          expectedAnswerBoolean: expectedAnswer === 'true',
          followupTrigger,
          followupText: followupTrigger !== 'NONE' ? followupText.trim() : null,
        }),
      };
      await onSave(payload);
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      style={{ background: 'var(--ids-palette-information-95)', border: '1px solid var(--ids-palette-neutral-90)', borderRadius: '6px', padding: '1rem', marginTop: '0.75rem' }}
    >
      <div className="ids-mb-3">
        <IDSInput
          label={questionType === 'TEXT_BLOCK' ? 'Rubrik *' : 'Frågetext *'}
          type="text"
          value={questionText}
          onChange={e => { setQuestionText(e.target.value); if (textError) setTextError(null); }}
          invalid={!!textError}
          errorMsg={textError}
        />
      </div>

      {questionType !== 'TEXT_BLOCK' && (
        <div className="ids-mb-3">
          <IDSCheckbox
            checked={includeInTestResults}
            onChange={() => setIncludeInTestResults(value => !value)}
          >
            Spara svaret som ett testresultat
          </IDSCheckbox>
          {includeInTestResults && (
            <div className="ids-mt-2">
              <IDSInput
                label="Testfallsnamn (valfritt)"
                type="text"
                maxLength={255}
                value={testCaseName}
                onChange={e => setTestCaseName(e.target.value)}
                placeholder={questionText || 'Frågetexten används om fältet lämnas tomt'}
              />
              <p style={{ fontSize: '0.8rem', color: 'var(--ids-palette-neutral-40)', margin: '0.3rem 0 0' }}>
                Frågetexten används som testfallsnamn om fältet lämnas tomt.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="ids-mb-3">
        <p style={{ fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.9rem' }}>Typ</p>
        <div style={{ display: 'flex', gap: '1.5rem' }}>
          {QUESTION_TYPE_OPTIONS.map(([val, label]) => (
            <IDSRadio
              key={val}
              name={`qtype-${sectionId}-${initial?.id ?? 'new'}`}
              value={val}
              checked={questionType === val}
              onChange={() => setQuestionType(val)}
            >
              {label}
            </IDSRadio>
          ))}
        </div>
      </div>

      {questionType !== 'TEXT_BLOCK' && (
        <div className="ids-mb-3">
          <p style={{ fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.9rem' }}>Måste besvaras?</p>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            {[['true', 'Obligatorisk'], ['false', 'Valfri']].map(([val, label]) => (
              <IDSRadio
                key={val}
                name={`mandatory-${sectionId}-${initial?.id ?? 'new'}`}
                value={val}
                checked={String(mandatory) === val}
                onChange={() => setMandatory(val === 'true')}
              >
                {label}
              </IDSRadio>
            ))}
          </div>
        </div>
      )}

      <div className="ids-mb-3">
        <IDSTextarea
          label="Infotext (visas för användaren med info-ikon, valfri)"
          rows={2}
          value={info}
          onChange={e => setInfo(e.target.value)}
          style={{ width: '100%', resize: 'vertical' }}
        />
      </div>

      {questionType === 'TEXT_BLOCK' && (
        <div className="ids-mb-3">
          <IDSTextarea
            label="Beskrivningstext (valfri)"
            rows={3}
            value={bodyText}
            onChange={e => setBodyText(e.target.value)}
            style={{ width: '100%', resize: 'vertical' }}
          />
        </div>
      )}

      {questionType === 'YES_NO' && (
        <>
          <div className="ids-mb-3">
            <p style={{ fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.9rem' }}>Förväntat svar</p>
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              {[['true', 'Ja'], ['false', 'Nej']].map(([val, label]) => (
                <IDSRadio
                  key={val}
                  name={`exp-${sectionId}-${initial?.id ?? 'new'}`}
                  value={val}
                  checked={expectedAnswer === val}
                  onChange={() => setExpectedAnswer(val)}
                >
                  {label}
                </IDSRadio>
              ))}
            </div>
          </div>

          <div className="ids-mb-3">
            <IDSSelect label="Följdfråga" value={followupTrigger} onChange={e => setFollowupTrigger(e.target.value)}>
              <option value="NONE">Ingen följdfråga</option>
              <option value="ON_YES">Vid svar Ja</option>
              <option value="ON_NO">Vid svar Nej</option>
            </IDSSelect>
          </div>

          {followupTrigger !== 'NONE' && (
            <div className="ids-mb-3">
              <IDSInput label="Följdfrågetext" type="text" value={followupText} onChange={e => setFollowupText(e.target.value)} />
            </div>
          )}
        </>
      )}

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <IDSButton size="s" type="submit" disabled={loading}>{loading ? 'Sparar...' : submitLabel}</IDSButton>
        <IDSButton size="s" type="button" onClick={onCancel}>Avbryt</IDSButton>
      </div>
      <StatusMsg error={error} />
    </form>
  );
};

// ── Question row ──────────────────────────────────────────────────────────────

const QuestionRow = ({ question, sectionId, isFirst, isLast, onUpdate, onDelete, onMoved }) => {
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [moving, setMoving] = useState(false);

  const handleMove = async (dir) => {
    setMoving(true);
    try {
      await moveQuestion(sectionId, question.id, dir);
      onMoved(question.id, dir);
    } finally {
      setMoving(false);
    }
  };

  const typeLabel = QUESTION_TYPE_LABELS[question.questionType] || 'Ja/Nej';
  const isTextLike = TEXT_LIKE_TYPES.includes(question.questionType);
  const typeBadge = (
    <span style={{
      display: 'inline-block', fontSize: '0.72rem', fontWeight: 600,
      padding: '1px 7px', borderRadius: '10px', marginLeft: '0.5rem',
      background: isTextLike ? 'var(--ids-palette-information-90)' : question.questionType === 'TEXT_BLOCK' ? 'var(--ids-palette-warning-90, #fff3cd)' : 'var(--ids-palette-neutral-90)',
      color: isTextLike ? 'var(--ids-palette-information-30)' : question.questionType === 'TEXT_BLOCK' ? 'var(--ids-palette-warning-30, #856404)' : 'var(--ids-palette-neutral-30)',
    }}>{typeLabel}</span>
  );

  return (
    <div style={{ borderBottom: '1px solid var(--ids-palette-neutral-90)', paddingBottom: '0.6rem', marginBottom: '0.6rem' }}>
      {editing ? (
        <QuestionForm
          sectionId={sectionId}
          initial={question}
          onSave={async (payload) => {
            const updated = await updateQuestion(sectionId, question.id, payload);
            onUpdate(updated);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
          submitLabel="Uppdatera"
        />
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              <strong style={{ marginRight: '0.35rem' }}>{question.orderIndex}.</strong>
              {question.questionText}
              {typeBadge}
              {question.includeInTestResults && <IDSBadge type="info">Testfall</IDSBadge>}
            </p>
            {question.includeInTestResults && (
              <p style={{ margin: '0.25rem 0 0 1.4rem', fontSize: '0.8rem', color: 'var(--ids-palette-neutral-40)' }}>
                Testfallsnamn: {question.testCaseName || question.questionText}
              </p>
            )}
            {question.questionType === 'YES_NO' && question.followupConfig?.trigger !== 'NONE' && (
              <p style={{ margin: '0.25rem 0 0 1.4rem', fontSize: '0.8rem', color: 'var(--ids-palette-neutral-40)', fontStyle: 'italic' }}>
                Följdfråga ({question.followupConfig.trigger === 'ON_YES' ? 'vid Ja' : 'vid Nej'}): {question.followupConfig.text}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.3rem', flexShrink: 0 }}>
            <IDSButton size="xs" type="button" secondary disabled={isFirst || moving} onClick={() => handleMove('UP')}>↑</IDSButton>
            <IDSButton size="xs" type="button" secondary disabled={isLast || moving} onClick={() => handleMove('DOWN')}>↓</IDSButton>
            <IDSButton size="xs" type="button" secondary onClick={() => setEditing(true)}>Redigera</IDSButton>
            <IDSButton size="xs" type="button" onClick={() => setConfirmDelete(true)}>Ta bort</IDSButton>
          </div>
        </div>
      )}
      <ConfirmDialog
        isOpen={confirmDelete}
        onConfirm={async () => { await deleteQuestion(sectionId, question.id); onDelete(question.id); setConfirmDelete(false); }}
        onCancel={() => setConfirmDelete(false)}
        message={`Ta bort frågan "${question.questionText}"?`}
      />
    </div>
  );
};

// ── Section accordion ─────────────────────────────────────────────────────────

const SectionAccordion = ({ section, isFirst, isLast, contractId, onSectionUpdate, onSectionDelete, onSectionMoved, onQuestionAdded }) => {
  const [editing, setEditing] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [movingSection, setMovingSection] = useState(false);

  const sortedQuestions = [...(section.questions || [])].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

  const handleQuestionUpdate = (updated) => {
    onSectionUpdate(section.id, {
      questions: section.questions.map(q => q.id === updated.id ? updated : q),
    });
  };

  const handleQuestionDelete = (questionId) => {
    onSectionUpdate(section.id, { questions: section.questions.filter(q => q.id !== questionId) });
  };

  const handleQuestionMoved = (questionId, direction) => {
    onSectionUpdate(section.id, {
      questions: section.questions.map(q => {
        const sorted = [...section.questions].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
        const idx = sorted.findIndex(sq => sq.id === questionId);
        const swapIdx = direction === 'UP' ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= sorted.length) return q;
        if (q.id === sorted[idx].id) return { ...q, orderIndex: sorted[swapIdx].orderIndex ?? swapIdx };
        if (q.id === sorted[swapIdx].id) return { ...q, orderIndex: sorted[idx].orderIndex ?? idx };
        return q;
      }),
    });
  };

  const handleMoveSection = async (dir) => {
    setMovingSection(true);
    try {
      await moveQuestionSection(section.id, dir);
      onSectionMoved(section.id, dir);
    } finally {
      setMovingSection(false);
    }
  };

  const scenarioLabel = { ALL: 'Båda', WEBAPP_ONLY: 'S1', LOCAL_ONLY: 'S3' }[section.applicableScenario || 'ALL'] || 'Båda';
  const scenarioColor = { ALL: 'var(--ids-palette-neutral-85)', WEBAPP_ONLY: 'var(--ids-palette-information-90)', LOCAL_ONLY: 'var(--ids-palette-success-90, #d4edda)' }[section.applicableScenario || 'ALL'];

  return (
    <div style={{ border: '1px solid var(--ids-palette-neutral-85)', borderRadius: '6px', marginBottom: '0.75rem', overflow: 'hidden' }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: 'var(--ids-palette-neutral-97)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 600, fontSize: '0.95rem' }}>
            {section.title}
            <span style={{ fontWeight: 400, color: 'var(--ids-palette-neutral-50)', marginLeft: '0.5rem', fontSize: '0.82rem' }}>
              {sortedQuestions.length} frågor
            </span>
            <span style={{
              display: 'inline-block', fontSize: '0.7rem', fontWeight: 700,
              padding: '1px 6px', borderRadius: '8px', marginLeft: '0.5rem',
              background: scenarioColor, color: 'var(--ids-palette-neutral-20)',
            }}>{scenarioLabel}</span>
          </p>
          {section.description && (
            <p style={{ margin: '0.15rem 0 0', fontSize: '0.82rem', color: 'var(--ids-palette-neutral-40)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {section.description}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.3rem', flexShrink: 0 }}>
          <IDSButton size="xs" type="button" secondary disabled={isFirst || movingSection} onClick={() => handleMoveSection('UP')}>↑</IDSButton>
          <IDSButton size="xs" type="button" secondary disabled={isLast || movingSection} onClick={() => handleMoveSection('DOWN')}>↓</IDSButton>
          <IDSButton size="xs" type="button" secondary onClick={() => setEditing(!editing)}>{editing ? 'Avbryt' : 'Redigera'}</IDSButton>
          <IDSButton size="xs" type="button" onClick={() => setConfirmDelete(true)}>Ta bort</IDSButton>
        </div>
      </div>

      {/* Section edit form */}
      {editing && (
        <div style={{ padding: '1rem', borderTop: '1px solid var(--ids-palette-neutral-90)' }}>
          <SectionForm
            initial={section}
            onSave={async (data) => {
              const updated = await updateQuestionSection(section.id, data);
              onSectionUpdate(section.id, { title: updated.title, description: updated.description });
              setEditing(false);
            }}
            onCancel={() => setEditing(false)}
            submitLabel="Uppdatera"
          />
        </div>
      )}

      {/* Questions list */}
      <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid var(--ids-palette-neutral-90)' }}>
        {sortedQuestions.length === 0 && (
          <p style={{ margin: '0 0 0.75rem', color: 'var(--ids-palette-neutral-60)', fontSize: '0.875rem' }}>Inga frågor ännu.</p>
        )}

        {sortedQuestions.map((q, i) => (
          <QuestionRow
            key={q.id}
            question={q}
            sectionId={section.id}
            isFirst={i === 0}
            isLast={i === sortedQuestions.length - 1}
            onUpdate={handleQuestionUpdate}
            onDelete={handleQuestionDelete}
            onMoved={handleQuestionMoved}
          />
        ))}

        {showQuestionForm ? (
          <QuestionForm
            sectionId={section.id}
            onSave={async (payload) => {
              const q = await addQuestion(section.id, payload);
              onQuestionAdded(section.id, q);
              setShowQuestionForm(false);
            }}
            onCancel={() => setShowQuestionForm(false)}
            submitLabel="Lägg till"
          />
        ) : (
          <IDSButton size="xs" type="button" secondary onClick={() => setShowQuestionForm(true)}>+ Lägg till fråga</IDSButton>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDelete}
        onConfirm={async () => {
          await deleteQuestionSection(section.id);
          await unlinkSectionFromContract(section.id, contractId);
          onSectionDelete(section.id);
          setConfirmDelete(false);
        }}
        onCancel={() => setConfirmDelete(false)}
        message={`Ta bort sektionen "${section.title}" och alla dess frågor?`}
      />
    </div>
  );
};

// ── Form preview (read-only) ──────────────────────────────────────────────────

const FormPreview = ({ sections, contractName, onClose }) => {
  const [previewScenario, setPreviewScenario] = useState('webapp');

  const filtered = [...sections]
    .filter(s => {
      const sc = s.applicableScenario || 'ALL';
      if (sc === 'ALL') return true;
      return previewScenario === 'local' ? sc === 'LOCAL_ONLY' : sc === 'WEBAPP_ONLY';
    })
    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

  return (
    <IDSDialog show persistent headline={`Förhandsgranskning – ${contractName}`} maxWidth="760px">
      {/* Scenario switcher */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
        {[['webapp', 'Testrapport'], ['local', 'Självdeklaration']].map(([val, label]) => (
          <button
            key={val}
            type="button"
            onClick={() => setPreviewScenario(val)}
            style={{
              padding: '0.35rem 1rem', fontSize: '0.875rem', borderRadius: '4px',
              border: '1px solid var(--ids-color-primary, #0065bd)',
              background: previewScenario === val ? 'var(--ids-color-primary, #0065bd)' : 'transparent',
              color: previewScenario === val ? '#fff' : 'var(--ids-color-primary, #0065bd)',
              cursor: 'pointer',
            }}
          >{label}</button>
        ))}
      </div>

      <div style={{ maxHeight: '65vh', overflowY: 'auto', paddingRight: '0.5rem' }}>
        {filtered.length === 0 && (
          <p style={{ color: 'var(--ids-palette-neutral-50)' }}>Inga sektioner att visa för detta scenario.</p>
        )}

        {filtered.map((section) => {
          const questions = [...(section.questions || [])].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

          return (
            <div key={section.id} style={{ marginBottom: '2rem' }}>
              <h3 style={{
                fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.25rem',
                color: 'var(--ids-color-primary, #0065bd)',
                borderBottom: '2px solid var(--ids-color-primary, #0065bd)',
                paddingBottom: '0.3rem',
              }}>
                {section.title}
              </h3>
              {section.description && (
                <p style={{ margin: '0.3rem 0 0.75rem', fontSize: '0.875rem', color: 'var(--ids-palette-neutral-40)', fontStyle: 'italic' }}>
                  {section.description}
                </p>
              )}

              {questions.map((q) => {
                if (q.questionType === 'TEXT_BLOCK') {
                  return (
                    <div key={q.id} style={{
                      borderLeft: '3px solid var(--ids-color-primary, #0065bd)',
                      paddingLeft: '0.85rem', marginTop: '1rem', marginBottom: '0.75rem',
                    }}>
                      <p style={{ fontWeight: 600, fontSize: '0.95rem', margin: 0 }}>{q.questionText}</p>
                      {q.bodyText && (
                        <p style={{ margin: '0.2rem 0 0', fontSize: '0.875rem', color: 'var(--ids-palette-neutral-30)', whiteSpace: 'pre-wrap' }}>
                          {q.bodyText}
                        </p>
                      )}
                    </div>
                  );
                }

                if (q.questionType === 'TEXT' || q.questionType === 'SERVICE_PROVIDER') {
                  return (
                    <div key={q.id} style={{ marginBottom: '1.25rem' }}>
                      <p style={{ marginBottom: '0.4rem', fontSize: '0.9rem' }}>{q.questionText}</p>
                      <IDSTextarea
                        block
                        light
                        label={q.questionText}
                        rows={3}
                        readOnly
                        placeholder="Fritextsvar..."
                      />
                    </div>
                  );
                }

                if (q.questionType === 'DATE') {
                  return (
                    <div key={q.id} style={{ marginBottom: '1.25rem' }}>
                      <p style={{ marginBottom: '0.4rem', fontSize: '0.9rem' }}>{q.questionText}</p>
                      <IDSDatePicker readOnly />
                    </div>
                  );
                }

                if (q.questionType === 'SOURCE_SYSTEM') {
                  return (
                    <div key={q.id} style={{ marginBottom: '1.25rem' }}>
                      <p style={{ marginBottom: '0.4rem', fontSize: '0.9rem' }}>{q.questionText}</p>
                      <IDSInput type="text" light readOnly label="Källsystem" placeholder="Väljs ur Jiras källsystemslista..." />
                      <div className="ids-mt-3">
                        <IDSInput type="text" light readOnly label="Version/steg" placeholder="Fritext, t.ex. R8.3..." />
                      </div>
                    </div>
                  );
                }

                // YES_NO
                return (
                  <div key={q.id} style={{ marginBottom: '1.25rem' }}>
                    <p style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>{q.questionText}</p>
                    <div style={{ display: 'flex', gap: '1.5rem' }}>
                      {['Ja', 'Nej'].map(label => (
                        <label key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem', color: 'var(--ids-palette-neutral-50)', cursor: 'not-allowed' }}>
                          <input type="checkbox" disabled style={{ cursor: 'not-allowed' }} />
                          {label}
                        </label>
                      ))}
                    </div>
                    {q.followupConfig?.trigger !== 'NONE' && q.followupConfig?.text && (
                      <p style={{ marginTop: '0.4rem', fontSize: '0.8rem', color: 'var(--ids-palette-neutral-60)', fontStyle: 'italic' }}>
                        ↳ Följdfråga ({q.followupConfig.trigger === 'ON_YES' ? 'vid Ja' : 'vid Nej'}): {q.followupConfig.text}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div slot="buttons">
        <IDSButton type="button" onClick={onClose}>Stäng förhandsgranskning</IDSButton>
      </div>
    </IDSDialog>
  );
};

// ── Main layout ───────────────────────────────────────────────────────────────

const ContractQuestionsAdminLayout = () => {
  const [contracts, setContracts] = useState([]);
  const [selectedContractId, setSelectedContractId] = useState('');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contractsLoading, setContractsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Load contracts on mount
  useEffect(() => {
    fetchContracts()
      .then(setContracts)
      .catch(err => setError(err.message || 'Kunde inte ladda kontrakt. Ladda om sidan.'))
      .finally(() => setContractsLoading(false));
  }, []);

  // Load sections when a contract is selected
  const loadSections = useCallback(async (contractId) => {
    if (!contractId) { setSections([]); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSectionsByContract(contractId);
      setSections(data);
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleContractChange = (e) => {
    const id = e.target.value;
    setSelectedContractId(id);
    setShowCreateForm(false);
    setSections([]);
    loadSections(id);
  };

  const sortedSections = [...sections].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

  const selectedContract = contracts.find(c => String(c.id) === String(selectedContractId));

  // Create a new section AND immediately link it to the selected contract
  const handleSectionCreated = async ({ title, description }) => {
    const created = await createQuestionSection({ title, description });
    await linkSectionToContract(created.id, selectedContractId);
    setSections(prev => [...prev, { ...created, questions: [], linkedContracts: [] }]);
    setShowCreateForm(false);
  };

  const handleSectionUpdate = (sectionId, patch) => {
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, ...patch } : s));
  };

  const handleSectionDelete = (sectionId) => {
    setSections(prev => prev.filter(s => s.id !== sectionId));
  };

  const handleSectionMoved = (sectionId, direction) => {
    setSections(prev => {
      const sorted = [...prev].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
      const idx = sorted.findIndex(s => s.id === sectionId);
      const swapIdx = direction === 'UP' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= sorted.length) return prev;
      const tmp = sorted[idx].orderIndex ?? idx;
      sorted[idx] = { ...sorted[idx], orderIndex: sorted[swapIdx].orderIndex ?? swapIdx };
      sorted[swapIdx] = { ...sorted[swapIdx], orderIndex: tmp };
      return sorted;
    });
  };

  const handleQuestionAdded = (sectionId, question) => {
    setSections(prev => prev.map(s =>
      s.id === sectionId ? { ...s, questions: [...(s.questions || []), question] } : s
    ));
  };

  return (
    <div>
      {/* Preview dialog */}
      {showPreview && (
        <FormPreview
          sections={sections}
          contractName={selectedContract ? `${selectedContract.contractName} ${selectedContract.version}` : ''}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Contract selector */}
      <div style={{ marginBottom: '1.5rem' }}>
        <p className="ids-mb-2" style={{ fontWeight: 600 }}>Välj tjänstekontrakt</p>
        {contractsLoading ? (
          <p style={{ color: 'var(--ids-palette-neutral-50)', fontSize: '0.9rem' }}>Laddar kontrakt...</p>
        ) : (
          <IDSSelect
            label="Tjänstekontrakt"
            value={selectedContractId}
            onChange={handleContractChange}
            style={{ maxWidth: '400px' }}
          >
            <option value="">– Välj ett kontrakt –</option>
            {contracts.map(c => (
              <option key={c.id} value={c.id}>
                {c.contractName} {c.version}
              </option>
            ))}
          </IDSSelect>
        )}
      </div>

      {/* Content area */}
      {!selectedContractId && (
        <div style={{
          padding: '2.5rem', textAlign: 'center',
          background: 'var(--ids-palette-neutral-97)', borderRadius: '8px',
          border: '1px dashed var(--ids-palette-neutral-80)',
        }}>
          <p style={{ color: 'var(--ids-palette-neutral-50)', fontSize: '0.95rem', margin: 0 }}>
            Välj ett tjänstekontrakt ovan för att se och administrera dess sektioner och frågor.
          </p>
        </div>
      )}

      {selectedContractId && (
        <>
          {/* Header row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>
              <h2 className="ids-heading-m" style={{ margin: 0 }}>
                {selectedContract ? `${selectedContract.contractName} ${selectedContract.version}` : 'Kontrakt'}
              </h2>
              <p style={{ margin: '0.2rem 0 0', fontSize: '0.85rem', color: 'var(--ids-palette-neutral-50)' }}>
                {sortedSections.length} sektion{sortedSections.length !== 1 ? 'er' : ''} kopplade till detta kontrakt
              </p>
            </div>
            {!showCreateForm && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <IDSButton size="s" type="button" secondary onClick={() => setShowPreview(true)}>
                  👁 Förhandsgranska
                </IDSButton>
                <IDSButton size="s" type="button" onClick={() => setShowCreateForm(true)}>
                  + Skapa ny sektion
                </IDSButton>
              </div>
            )}
          </div>

          {/* Create form */}
          {showCreateForm && (
            <SectionForm
              onSave={handleSectionCreated}
              onCancel={() => setShowCreateForm(false)}
              submitLabel="Skapa och koppla"
            />
          )}

          {/* Loading / error */}
          {loading && <p style={{ color: 'var(--ids-palette-neutral-50)' }}>Laddar sektioner...</p>}
          {error && (
            <IDSAlert type="error">
              <p>{error}</p>
            </IDSAlert>
          )}

          {/* Empty state */}
          {!loading && !error && sortedSections.length === 0 && (
            <div style={{
              padding: '2rem', textAlign: 'center',
              background: 'var(--ids-palette-neutral-97)', borderRadius: '8px',
              border: '1px dashed var(--ids-palette-neutral-80)',
            }}>
              <p style={{ color: 'var(--ids-palette-neutral-50)', margin: 0 }}>
                Inga sektioner kopplade till detta kontrakt ännu. Skapa en ny sektion ovan.
              </p>
            </div>
          )}

          {/* Sections */}
          {sortedSections.map((section, i) => (
            <SectionAccordion
              key={section.id}
              section={section}
              isFirst={i === 0}
              isLast={i === sortedSections.length - 1}
              contractId={selectedContractId}
              onSectionUpdate={handleSectionUpdate}
              onSectionDelete={handleSectionDelete}
              onSectionMoved={handleSectionMoved}
              onQuestionAdded={handleQuestionAdded}
            />
          ))}
        </>
      )}
    </div>
  );
};

export default ContractQuestionsAdminLayout;
